import { useQuery } from '@apollo/client';
import {
  AccessControl,
  ALL_MODULES,
  AttachmentPreviewer,
  BackgroundIcon,
  CardPage,
  ChooseAttachments,
  getDownloadURL,
  getHeaderValue,
  getTranslatedText,
  Loader,
  NoDataCard,
  toast,
  fetchApi
} from '@nile/components';
import { RMMClient } from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import DeleteModal from '@nile/projects/src/Components/Modals/DeleteModal';
import {
  GET_ASSET_ATTACHMENTS,
  GET_NON_MONITORED_ASSET_ATTACHMENTS
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import ITDocsContext from '@nile/rmm/src/modules/common/contexts/ITDocsListContext';
import AttachmentHolder from '@nile/rmm/src/modules/common/components/sub-pages/attachment-holder';
import {
  Modal,
  Link,
  Heading,
  Page,
  TextContainer,
  Button,
  Card
} from '@superops/ui-design-system';
import { cloneDeep, concat } from 'lodash';
import React, { useCallback, useEffect, useState, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';

function AssetAttachmentContainer(props) {
  const context = useContext(ITDocsContext);
  const { intl, assetId, isClientPortal, module } = props;
  const isAssetDetailsPage = module === ALL_MODULES.assetDetails;
  const [actionLoading, setActionLoading] = useState(false);

  const [addAttachmentsLoader, setAttachmentLoader] = useState(false);

  const [showAttachmentPreview, setShowAttachmentPreview] = useState({
    show: false,
    index: 0
  });

  let [formatterAttachment, setFormatterAttachment] = useState([]);

  const [assetAttachments, setAssetAttachments] = useState([]);

  /**
   *
   * Formatting attachment array for attachment previewer
   * @param {array} getAssetAttachments
   * @public
   */

  function formatAttachmentForPreview(getAssetAttachments) {
    formatterAttachment = getAssetAttachments.map((item) => {
      if (item)
        return {
          name: item.name,
          url: getAttachmentURL(item),
          type: item.name.substr(
            item.name.lastIndexOf('.') + 1,
            item.name.length
          )
        };
    });
    setFormatterAttachment(formatterAttachment);
  }

  const { loading } = useQuery(
    isAssetDetailsPage
      ? GET_ASSET_ATTACHMENTS
      : GET_NON_MONITORED_ASSET_ATTACHMENTS,
    {
      client: RMMClient,
      variables: {
        assetId
      },
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const responseData = isAssetDetailsPage
          ? res.getAssetAttachments
          : res.getNonMonitoredAssetAttachments;
        formatAttachmentForPreview(responseData);
        setAssetAttachments(responseData || []);
      }
    }
  );

  useEffect(() => {
    setShowAttachmentPreview(showAttachmentPreview);
  }, [showAttachmentPreview.show]);

  const [deleteAttachmentmodal, setDeleteAttachmentmodal] = useState({
    showModal: false,
    attachmentId: ''
  });
  const delModal = React.createRef();

  /**
   * @description
   * Gets called while uploading the attachments in a particular asset
   * @param
   * {Array} docs - array of uploaded files
   * @param
   * {function} callback
   * @return
   * {void}
   */
  const uploadDocuments = (docs, callback) => {
    if (docs) {
      setActionLoading(true);
      setAttachmentLoader(true);
      const fileData = new FormData();
      const detailsObj = {
        assetId
      };
      docs.forEach((doc) => {
        fileData.append('module', 'ASSET_ATTACHMENT');
        fileData.append(`files`, doc);
        fileData.append('details', JSON.stringify(detailsObj));
      });
      const requestOptions = {
        method: 'POST',
        credentials: 'include',
        body: fileData,
        headers: getHeaderValue()
      };
      context?.setToggleAttachmentsPopup &&
        context?.setToggleAttachmentsPopup(false);
      fetchApi(`${envConfig.fileRoot}/upload`, requestOptions)
        .then((response) => {
          return response.json();
        })
        .then((uploadedDocs) => {
          console.log('getting uploaded documents', uploadedDocs);
          setActionLoading(false);
          if (uploadedDocs?.data?.length) {
            const updatedDocuments = concat(
              uploadedDocs.data,
              cloneDeep(assetAttachments)
            );
            formatAttachmentForPreview(updatedDocuments);
            setAssetAttachments(updatedDocuments);
            setAttachmentLoader(false);
            toast('success', 'asset.details.attachments.upload.success');
          } else {
            toast('error', 'Something went wrong');
          }
          callback && callback();
        });
    }
  };

  const deleteAttachment = (attachmentID) => {
    // const reqBody = {id: attachmentID};
    const requestOptions = {
      method: 'DELETE',
      credentials: 'include',
      headers: getHeaderValue()
    };
    fetchApi(
      `${envConfig.rmmRoot}/asset/attachment/${attachmentID}`,
      requestOptions
    ).then(() => {
      const clonedAttachments = cloneDeep(assetAttachments);
      const filteredAttachments = clonedAttachments.filter((attachment) => {
        return attachment.attachmentId !== attachmentID;
      });
      setAssetAttachments(filteredAttachments);
      formatAttachmentForPreview(filteredAttachments);
      toast('success', 'asset.details.attachments.delete.success');
    });
  };

  /**
   * @description
   * Renders the attachment name and on clicking downloads the attachment
   * @param {object}
   * attachment object
   * @return
   * attachment element
   */
  const getAttachmentURL = (attachment) => {
    const module = ALL_MODULES.assetAttachment;
    const details = {
      attachmentId: attachment.attachmentId
    };
    const url = getDownloadURL({ module, details });
    return url;
  };

  /**
   * @description
   * Closing attachment previewer
   */
  function closePreviwer() {
    setShowAttachmentPreview({
      ...showAttachmentPreview,
      show: false
    });
  }

  /**
   * @description
   * opening attachment previewer, if selected from a specific index then opening from the selected index.
   * @param {object} attachment
   * @param {number} index
   */

  function openAttachmentPreview(attachment, index) {
    setShowAttachmentPreview({
      ...showAttachmentPreview,
      show: true,
      index
    });
  }

  function handleDeleteModalChange() {
    setDeleteAttachmentmodal({
      showModal: !deleteAttachmentmodal.showModal,
      attachmentId: ''
    });
  }

  if (isClientPortal) {
    return (
      // eslint-disable-next-line react/jsx-filename-extension
      <>
        {loading && (
          <div className="fx full-height">
            <div className="margin-auto">
              <Loader />
            </div>
          </div>
        )}
        {assetAttachments.length > 0 && (
          <>
            <div className="mb-20">
              <Heading element="h2">
                <FormattedMessage id="asset.details.agent.attachments" />
              </Heading>
            </div>
            <div className="flex wrap">
              {assetAttachments.map((attachment, index) => {
                if (attachment)
                  return (
                    <div
                      className="mb10 mr-20 f32"
                      key={attachment.attachmentId}
                    >
                      <Card noShadow variant="secondaryCard" AdaptHeight>
                        <div className="flex actr p10">
                          <div className="mr-10">
                            <SVGIcon name="Download" />
                          </div>
                          <Link
                            removeUnderline
                            url={getAttachmentURL(attachment)}
                          >
                            {attachment.name}
                          </Link>
                        </div>
                      </Card>
                    </div>
                  );
              })}
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <CardPage isNewLayout fullHeight className="full-height white-grey-page">
      <Page
        variant="primary"
        title={<FormattedMessage id="asset.details.agent.attachments" />}
        primaryAction={
          <AccessControl
            roleToCheck="fileAttachments.add"
            render={
              <AssetAttachments
                intl={intl}
                loading={actionLoading}
                handleUpload={uploadDocuments}
              />
            }
          ></AccessControl>
        }
      >
        <div className="full-height">
          {loading && (
            <div className="fx full-height">
              <div className="margin-auto">
                <Loader />
              </div>
            </div>
          )}

          {assetAttachments.length > 0 ? (
            <div className="w100 flex wrap">
              {assetAttachments.map((attachment, index) => {
                if (attachment)
                  return (
                    <div className="w50 p5 mb10">
                      <AttachmentHolder
                        onClick={() => openAttachmentPreview(attachment, index)}
                        fileName={attachment.name || ''}
                        onDelete={() =>
                          setDeleteAttachmentmodal({
                            showModal: true,
                            attachmentId: attachment.attachmentId
                          })
                        }
                      />
                    </div>
                  );
              })}

              <DeleteModal
                reference={delModal}
                description={getTranslatedText(
                  intl,
                  'asset.details.attachments.delete.message'
                )}
                handleDeleteConfirmation={() => {
                  deleteAttachment(deleteAttachmentmodal.attachmentId);
                  setDeleteAttachmentmodal({
                    showModal: false,
                    attachmentId: ''
                  });
                }}
                title={getTranslatedText(
                  intl,
                  'asset.details.attachments.delete.title'
                )}
                active={deleteAttachmentmodal.showModal}
                handleChange={handleDeleteModalChange}
              />
            </div>
          ) : (
            <div className="flex center jcenter full-height">
              <NoDataCard
                imageURL={`url(${`${envConfig.imageRoot}svg/fileUpload.svg`})`}
                description={
                  <FormattedMessage id="asset.details.agent.noAttachment" />
                }
              />
            </div>
          )}
        </div>
        {showAttachmentPreview.show && (
          <AttachmentPreviewer
            showPreviewer={showAttachmentPreview.show}
            closePreviwer={closePreviwer}
            list={formatterAttachment}
            selectedIndex={showAttachmentPreview.index}
          />
        )}
      </Page>
    </CardPage>
  );

  function AssetAttachments(props) {
    const context = useContext(ITDocsContext);
    const [active, setActive] = useState(false);

    const [uploadedFiles, setUploadedFiles] = useState(null);

    const { modalActivator, intl } = props;

    const [loading, setLoading] = useState(props.loading);

    useEffect(() => {
      setLoading(props.loading);
    }, [props.loading]);

    const handleChange = useCallback(() => {
      setActive((prev) => !prev);
      context?.setToggleAttachmentsPopup &&
        context?.setToggleAttachmentsPopup(false);
    }, [active]);

    useEffect(() => {
      if (context?.toggleAttachmentsPopup) {
        setActive(true);
      }
    }, [context?.toggleAttachmentsPopup]);

    const uploadDocuments = useCallback(() => {
      props.handleUpload(uploadedFiles, handleChange);
    });

    const handleCancel = useCallback(() => {
      setUploadedFiles(null);
      handleChange();
    });

    const updateFiles = (updatedFiles) => {
      setUploadedFiles(updatedFiles);
    };
    return (
      <div>
        {modalActivator ? (
          <div onClick={handleChange} id={props.id}>
            {modalActivator}
          </div>
        ) : (
          <Button
            // customClassName="attachment-upload-block"
            onClick={handleChange}
            id={props.id}
            loading={props.loading}
            icon={
              <div className="fx center">
                <SVGIcon name="FileAttachment" />
              </div>
            }
          >
            {getTranslatedText(intl, 'asset.details.attach.files')}
          </Button>
        )}
        <Modal
          //activator={activator}//check why this is not rendering
          open={active}
          onClose={handleChange}
          title={
            props.title ||
            getTranslatedText(intl, 'asset.details.attachments.uploadHeader')
          }
          primaryAction={{
            content: getTranslatedText(
              intl,
              'asset.details.attachments.uploadBtn'
            ),
            loading: loading,
            onAction: uploadDocuments
          }}
          secondaryActions={[
            {
              content: getTranslatedText(intl, 'common.cancel'),
              onAction: handleCancel
            }
          ]}
        >
          <Modal.Section>
            <TextContainer>
              <p>
                {props.description ||
                  getTranslatedText(
                    intl,
                    'asset.details.attachments.selectDoc'
                  )}
              </p>
            </TextContainer>
            <ChooseAttachments updateFiles={updateFiles} limitFileSize={100} />
          </Modal.Section>
        </Modal>
      </div>
    );
  }
}

export { AssetAttachmentContainer };
